import { type MessageDescriptor } from 'react-intl';

import { jsmPortalPlaygroundHrMessages as messages } from './jsm-portal-playground-hr-messages';

const surveyViews = 118;

export type SurveyChoiceId =
	| 'veryDissatisfied'
	| 'dissatisfied'
	| 'neutral'
	| 'satisfied'
	| 'verySatisfied';

export type SurveyChoice = {
	id: SurveyChoiceId;
	label: MessageDescriptor;
	/**
	 * The designed bar height in px against the chart's 206px canvas. Figma drew the bars and the
	 * response counts independently, so this is deliberately not derived from `responses`.
	 */
	barHeight: number;
	responses: number;
	/** Fraction of all responses, derived from `responses`. Formatted as a percentage at render. */
	share: number;
};

export const SURVEY_CHART_HEIGHT = 206;

const surveyChoiceSources: Array<Omit<SurveyChoice, 'share'>> = [
	{
		id: 'veryDissatisfied',
		label: messages.choiceVeryDissatisfied,
		barHeight: 41.92,
		responses: 2,
	},
	{
		id: 'dissatisfied',
		label: messages.choiceDissatisfied,
		barHeight: 87.43,
		responses: 8,
	},
	{ id: 'neutral', label: messages.choiceNeutral, barHeight: 116.17, responses: 20 },
	{
		id: 'satisfied',
		label: messages.choiceSatisfied,
		barHeight: 184.44,
		responses: 30,
	},
	{
		id: 'verySatisfied',
		label: messages.choiceVerySatisfied,
		barHeight: 80.24,
		responses: 12,
	},
];

const surveyResponseTotal = surveyChoiceSources.reduce((sum, choice) => sum + choice.responses, 0);

export const surveyChoices: SurveyChoice[] = surveyChoiceSources.map((choice) => ({
	...choice,
	share: surveyResponseTotal === 0 ? 0 : choice.responses / surveyResponseTotal,
}));

export const surveyTotals = {
	views: surveyViews,
	responses: surveyResponseTotal,
	/** Responses divided by views. */
	completionRate: surveyResponseTotal / surveyViews,
};

/** Top tick is the response total. The rest are evenly spaced as designed rather than to scale. */
export const surveyChartTicks = [surveyResponseTotal, 50, 25, 10, 0];

export const surveyChoiceLabels = Object.fromEntries(
	surveyChoices.map((choice) => [choice.id, choice.label]),
) as Record<SurveyChoiceId, MessageDescriptor>;

/** The `@atlaskit/lozenge` v11 appearance for each answer: positive, neutral or negative. */
export const surveyChoiceAppearances: Record<SurveyChoiceId, 'success' | 'default' | 'removed'> = {
	veryDissatisfied: 'removed',
	dissatisfied: 'removed',
	neutral: 'default',
	satisfied: 'success',
	verySatisfied: 'success',
};

export type SurveyResponseAnswers = {
	submitted: number;
	rewarding: MessageDescriptor;
};

export type SurveyResponse = {
	id: string;
	respondent: string;
	satisfaction: SurveyChoiceId;
	/**
	 * Only the top rows carry readable answers. The rest are drawn as grey bars so the eye settles on
	 * the real responses rather than scanning fabricated ones, and they cannot be opened.
	 */
	answers?: SurveyResponseAnswers;
};

export const surveyResponses: SurveyResponse[] = [
	{
		id: 'priya-hansra',
		respondent: 'Priya Hansra',
		satisfaction: 'verySatisfied',
		answers: {
			submitted: Date.UTC(2025, 9, 14, 9, 12),
			rewarding: messages.responsePriyaRewarding,
		},
	},
	{
		id: 'raul-gonzalez',
		respondent: 'Raul Gonzalez',
		satisfaction: 'satisfied',
		answers: {
			submitted: Date.UTC(2025, 9, 13, 16, 48),
			rewarding: messages.responseRaulRewarding,
		},
	},
	{
		id: 'olivia-yang',
		respondent: 'Olivia Yang',
		satisfaction: 'neutral',
		answers: {
			submitted: Date.UTC(2025, 9, 13, 11, 5),
			rewarding: messages.responseOliviaRewarding,
		},
	},
	{
		id: 'liam-estrada',
		respondent: 'Liam Estrada',
		satisfaction: 'satisfied',
		answers: {
			submitted: Date.UTC(2025, 9, 12, 14, 30),
			rewarding: messages.responseLiamRewarding,
		},
	},
	{ id: 'melanie-lee', respondent: 'Melanie Lee', satisfaction: 'dissatisfied' },
	{ id: 'robi-lopez', respondent: 'Robi Lopez', satisfaction: 'verySatisfied' },
	{ id: 'florence-garcia', respondent: 'Florence Garcia', satisfaction: 'neutral' },
	{ id: 'charles-rex', respondent: 'Charles Rex', satisfaction: 'veryDissatisfied' },
];

export const surveyInsight = {
	sentimentShare: 0.68,
	themes: [messages.themeTechnicalReadiness, messages.themeDocumentation, messages.themeMentorship],
};

/** How long Rovo "thinks" before the answer lands. */
export const INSIGHT_THINKING_MS = 2200;
/** How fast the answer types in. */
export const INSIGHT_CHARS_PER_SECOND = 120;
